import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { AuthContext } from '../auth/AuthContext';
import {
  getPermissionMatrix,
  putRolePermissions,
  listPermissionDelegates,
  grantUserPermission,
  revokeUserPermission,
} from '../api/permissionApi';
import { getUsers } from '../api/userApi';
import { ROLE_LABEL } from '../constants/roles';

const MATRIX_KEY = 'permissions.matrix.edit';

/**
 * Permission keys that the sidebar / app forces to be "Always on" regardless
 * of role. We still render them in the matrix so admins can see the contract,
 * but the checkbox is locked so toggling it doesn't create a misleading state.
 */
const ALWAYS_ON_PERM_KEYS = new Set(['nav.my_tasks']);

function roleOptionLabel(role) {
  if (ROLE_LABEL[role]) return ROLE_LABEL[role];
  return role.replace(/_/g, ' ');
}

export default function RolePermissionsPage() {
  const { user } = useContext(AuthContext);
  const qc = useQueryClient();
  const [selectedRole, setSelectedRole] = useState('');
  const [draftKeys, setDraftKeys] = useState(new Set());
  const [delegateUserId, setDelegateUserId] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['permission-matrix'],
    queryFn: getPermissionMatrix,
    enabled: !!user?.can_edit_matrix,
  });

  const roles = data?.roles || [];
  const permissions = data?.permissions || [];
  const matrix = data?.matrix || {};

  useEffect(() => {
    if (!selectedRole && roles.length) {
      const first = roles.find((r) => r !== 'superadmin') || roles[0];
      setSelectedRole(first);
    }
  }, [roles, selectedRole]);

  useEffect(() => {
    if (!selectedRole || !matrix[selectedRole]) return;
    setDraftKeys(new Set(matrix[selectedRole]));
  }, [selectedRole, matrix]);

  const saveMutation = useMutation({
    mutationFn: () => {
      // ALWAYS_ON keys are forced into every saved payload so the DB stays in
      // sync with what the matrix UI displays (the tile is shown checked even
      // when the role doesn't currently have it persisted).
      const merged = new Set(draftKeys);
      ALWAYS_ON_PERM_KEYS.forEach((k) => merged.add(k));
      return putRolePermissions(selectedRole, [...merged]);
    },
    onSuccess: () => {
      toast.success('Permissions saved');
      qc.invalidateQueries({ queryKey: ['permission-matrix'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const { data: delegatesData, refetch: refetchDelegates } = useQuery({
    queryKey: ['permission-delegates'],
    queryFn: () => listPermissionDelegates({ permission_key: MATRIX_KEY }),
    enabled: !!user?.can_delegate_grants,
  });

  const { data: usersList } = useQuery({
    queryKey: ['users'],
    queryFn: () => getUsers().then((r) => r.data || []),
    enabled: !!user?.can_delegate_grants,
  });

  /** Same queryKey ['users'] is used elsewhere; normalize if cache holds a raw axios response. */
  const usersArray = useMemo(() => {
    if (usersList == null) return [];
    if (Array.isArray(usersList)) return usersList;
    if (Array.isArray(usersList.data)) return usersList.data;
    return [];
  }, [usersList]);

  const delegateGrantees = useMemo(
    () => new Set((delegatesData?.users || []).map((u) => u.user_id)),
    [delegatesData],
  );

  const grantMutation = useMutation({
    mutationFn: (uid) => grantUserPermission(uid, MATRIX_KEY),
    onSuccess: () => {
      toast.success('Manager can now edit role permissions');
      refetchDelegates();
      setDelegateUserId('');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Grant failed'),
  });

  const revokeMutation = useMutation({
    mutationFn: (uid) => revokeUserPermission(uid, MATRIX_KEY),
    onSuccess: () => {
      toast.success('Access removed');
      refetchDelegates();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Revoke failed'),
  });

  if (!user?.can_edit_matrix) {
    return (
      <div style={{ padding: '2rem', maxWidth: 560 }}>
        <h1 style={{ fontSize: '1.25rem', color: '#0f172a' }}>Role permissions</h1>
        <p style={{ color: '#64748b', lineHeight: 1.5 }}>
          You do not have access to this page. Ask an admin to grant <code>permissions.matrix.edit</code> or use an
          admin account.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', color: '#b91c1c' }}>
        Failed to load permission matrix. Try again or check server logs.
      </div>
    );
  }

  const toggleKey = (key) => {
    setDraftKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectableUsers = usersArray.filter((u) => {
    const r = String(u.role || '').toLowerCase();
    if (r === 'superadmin' || r === 'admin') return false;
    return !delegateGrantees.has(u.id);
  });

  return (
    <div style={{ padding: '1.25rem 1.5rem', maxWidth: 960 }}>
      <h1 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem', color: '#0f172a' }}>Role permissions</h1>
      <p style={{ margin: '0 0 1.25rem', color: '#64748b', fontSize: '0.9rem', lineHeight: 1.45 }}>
        Superadmin always has every permission (not shown as editable). Changes apply on next navigation; users may need
        a refresh to see sidebar updates.
      </p>

      {user?.can_delegate_grants ? (
        <section
          style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            borderRadius: 10,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1rem', color: '#0f172a' }}>
            Delegates — who can edit this matrix
          </h2>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: '#64748b' }}>
            Admins and superadmins always can. Grant a manager (or other role) the same ability without making them
            admin.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <select
              value={delegateUserId}
              onChange={(e) => setDelegateUserId(e.target.value)}
              style={{
                minWidth: 240,
                padding: '0.45rem 0.5rem',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                fontSize: '0.85rem',
              }}
            >
              <option value="">Select user to grant…</option>
              {selectableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email} ({u.role})
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!delegateUserId || grantMutation.isPending}
              onClick={() => grantMutation.mutate(parseInt(delegateUserId, 10))}
              style={{
                padding: '0.45rem 0.9rem',
                borderRadius: 8,
                border: 'none',
                background: '#1272B3',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: delegateUserId ? 'pointer' : 'not-allowed',
                opacity: delegateUserId ? 1 : 0.5,
              }}
            >
              Grant matrix editor
            </button>
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.85rem', color: '#334155' }}>
            {(delegatesData?.users || []).map((u) => (
              <li key={u.user_id} style={{ marginBottom: 6 }}>
                {u.full_name || u.email}{' '}
                <span style={{ color: '#94a3b8' }}>
                  ({u.role}){' '}
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove matrix editor from ${u.email}?`)) {
                        revokeMutation.mutate(u.user_id);
                      }
                    }}
                    style={{
                      marginLeft: 8,
                      border: 'none',
                      background: 'none',
                      color: '#b91c1c',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                    }}
                  >
                    Revoke
                  </button>
                </span>
              </li>
            ))}
            {(delegatesData?.users || []).length === 0 ? (
              <li style={{ color: '#94a3b8' }}>No delegated editors yet.</li>
            ) : null}
          </ul>
        </section>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem' }}>Role</label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          disabled={selectedRole === 'superadmin'}
          style={{
            padding: '0.45rem 0.6rem',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            minWidth: 200,
            fontSize: '0.9rem',
          }}
        >
          {roles
            .filter((r) => r !== 'superadmin')
            .map((r) => (
              <option key={r} value={r}>
                {roleOptionLabel(r)}
              </option>
            ))}
        </select>
        <button
          type="button"
          disabled={!selectedRole || selectedRole === 'superadmin' || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          style={{
            padding: '0.45rem 1rem',
            borderRadius: 8,
            border: 'none',
            background: '#0f172a',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: selectedRole && selectedRole !== 'superadmin' ? 'pointer' : 'not-allowed',
            opacity: selectedRole && selectedRole !== 'superadmin' ? 1 : 0.45,
          }}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save role'}
        </button>
      </div>

      {selectedRole === 'superadmin' ? (
        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Superadmin is always fully permissioned.</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 8,
            maxHeight: 'min(60vh, 520px)',
            overflowY: 'auto',
            padding: '0.5rem 0',
          }}
        >
          {permissions.map((p) => {
            const alwaysOn = ALWAYS_ON_PERM_KEYS.has(p.perm_key);
            return (
              <label
                key={p.perm_key}
                title={alwaysOn ? 'Always on for every role — toggling has no effect.' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '0.45rem 0.5rem',
                  borderRadius: 8,
                  border: alwaysOn ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                  background: alwaysOn ? '#f0fdf4' : '#fff',
                  fontSize: '0.8rem',
                  cursor: alwaysOn ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={alwaysOn ? true : draftKeys.has(p.perm_key)}
                  disabled={alwaysOn}
                  onChange={() => {
                    if (alwaysOn) return;
                    toggleKey(p.perm_key);
                  }}
                />
                <span>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>
                    {p.label}
                    {alwaysOn && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: '#15803d',
                          background: '#dcfce7',
                          border: '1px solid #bbf7d0',
                          borderRadius: 999,
                          padding: '1px 6px',
                          verticalAlign: 'middle',
                        }}
                      >
                        Always on
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', wordBreak: 'break-all' }}>{p.perm_key}</div>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
